import { Injectable } from '@angular/core';
import { LambdaService } from './lambda.service';
import {
  buildProductFilesPayload,
  normalizeProductFiles,
  ProductPdfFilePayload,
} from './helpers/product-pdf-files';

export interface ProductCloneCreatePayload {
  productCode: string;
  shopId: number;
  productCategoryId: number;
  productSubCategoryId: number;
  productSubSubCategoryId: number;
  productIndustryId: number;
  productSupplierId: number;
  productCollectionId: number;
  productTaxId: number;
  productName: string;
  productDescription: string;
  productBaseCost: number;
  productSellPrice: number;
  productDiscountPrice: number;
  productCreationDate: string;
  productStatus: number;
  items: any[];
  variants: any[];
  imgURL: string;
  imgPosition: number;
  imgType: string;
  productType: number;
  productSessions: number;
  productFile: string;
  productFiles: ProductPdfFilePayload[];
  productPackageItems: number[];
  productConditions: string;
  externalUrl: string | null;
  externalRedirectEnabled: boolean;
  hidePrice: boolean;
  productPaymentMode: number;
}

export interface ProductCloneResult {
  payload: ProductCloneCreatePayload;
  response: any;
  productCode: string;
}

function toNumber(value: any, fallback: number = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toFlag(value: any, fallback: number = 0): number {
  if (value === true) return 1;
  if (value === false) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? (parsed ? 1 : 0) : fallback;
}

function toStringValue(value: any, fallback: string = ''): string {
  if (value == null) {
    return fallback;
  }
  return String(value);
}

function trimToNull(value: any): string | null {
  if (value == null) {
    return null;
  }

  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeRelationship(value: any): string[] | null {
  const relations = Array.isArray(value)
    ? value
    : toStringValue(value)
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

  const uniqueRelations = Array.from(new Set(relations.filter(Boolean)));
  return uniqueRelations.length > 0 ? uniqueRelations : null;
}

export function escapeProductCloneString(value: any): string {
  return toStringValue(value)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
}

export function buildClonedProductCode(originalCode: any): string {
  const baseCode = trimToNull(originalCode) ?? 'CLONE';
  return `${baseCode}-1`;
}

function buildProductCreationDate(now: Date = new Date()): string {
  return now.toLocaleString('en-US', {
    timeZone: 'America/Costa_Rica',
  });
}

function buildVariantValueLookup(variants: any[]): Map<string, string> {
  const lookup = new Map<string, string>();

  for (const variant of variants) {
    if (!Array.isArray(variant?.variantValues)) {
      continue;
    }

    for (const variantValue of variant.variantValues) {
      const variantValueName = trimToNull(variantValue?.variantValueName);
      const variantValueId = variantValue?.variantValueId;

      if (variantValueName && variantValueId != null) {
        lookup.set(String(variantValueId), variantValueName);
      }
    }
  }

  return lookup;
}

function normalizeVariants(rawVariants: any[]): any[] {
  return rawVariants
    .map((variant: any) => {
      const variantTypeName = trimToNull(
        variant?.variantTypeName ?? variant?.variantType
      );

      if (!variantTypeName || !Array.isArray(variant?.variantValues)) {
        return null;
      }

      const variantValues = variant.variantValues
        .map((variantValue: any, index: number) => {
          const variantValueName = trimToNull(
            typeof variantValue === 'string'
              ? variantValue
              : variantValue?.variantValueName
          );

          if (!variantValueName) {
            return null;
          }

          const variantValuePosition = Number.isFinite(
            Number(variantValue?.variantValuePosition)
          )
            ? Number(variantValue.variantValuePosition)
            : index;

          return {
            variantValueName,
            variantValuePosition,
          };
        })
        .filter((variantValue: any) => !!variantValue);

      return variantValues.length > 0
        ? {
            variantTypeName,
            variantValues,
          }
        : null;
    })
    .filter((variant: any) => !!variant);
}

function resolveItemVariantValues(
  item: any,
  variantValueLookup: Map<string, string>
): any[] | undefined {
  const rawValues = Array.isArray(item?.variantValues)
    ? item.variantValues
    : Array.isArray(item?.itemVariantValues)
    ? item.itemVariantValues
    : [];

  const values = rawValues
    .map((rawValue: any) => {
      if (rawValue == null) {
        return null;
      }

      if (typeof rawValue === 'object') {
        const variantValueName = trimToNull(rawValue.variantValueName);
        if (variantValueName) {
          return { variantValueName };
        }

        const variantValueId = rawValue.variantValueId ?? rawValue.id;
        if (variantValueId != null && variantValueLookup.has(String(variantValueId))) {
          return {
            variantValueName: variantValueLookup.get(String(variantValueId)),
          };
        }
      }

      if (
        (typeof rawValue === 'number' || typeof rawValue === 'string') &&
        variantValueLookup.has(String(rawValue))
      ) {
        return {
          variantValueName: variantValueLookup.get(String(rawValue)),
        };
      }

      const variantValueName = trimToNull(rawValue);
      return variantValueName ? { variantValueName } : null;
    })
    .filter((value: any) => !!value);

  const uniqueByName = Array.from(
    new Map(
      values.map((value: any) => [String(value.variantValueName), value])
    ).values()
  );

  return uniqueByName.length > 0 ? uniqueByName : undefined;
}

function resolveItemShopPoints(item: any, source: any): any[] {
  const sourceShopPoints = Array.isArray(source?.shopPoints) ? source.shopPoints : [];
  const itemId = item?.itemId;

  const matchedShopPoints = itemId == null
    ? []
    : sourceShopPoints.filter((shopPoint: any) => shopPoint?.itemId === itemId);

  if (matchedShopPoints.length > 0) {
    return matchedShopPoints.map((shopPoint: any) => ({
      shopPointId: toNumber(shopPoint?.shopPointId),
      itemQuantity: toNumber(shopPoint?.itemQuantity),
      itemUnlimited: toFlag(shopPoint?.itemUnlimited),
    }));
  }

  if (Array.isArray(item?.itemShopPoints)) {
    return item.itemShopPoints.map((shopPoint: any) => ({
      shopPointId: toNumber(shopPoint?.shopPointId),
      itemQuantity: toNumber(shopPoint?.itemQuantity),
      itemUnlimited: toFlag(shopPoint?.itemUnlimited),
    }));
  }

  if (item?.shopPointId != null) {
    return [
      {
        shopPointId: toNumber(item.shopPointId),
        itemQuantity: toNumber(item?.itemQuantity ?? item?.quantity),
        itemUnlimited: toFlag(item?.itemUnlimited),
      },
    ];
  }

  return [];
}

function normalizeItems(
  rawItems: any[],
  source: any,
  newProductCode: string,
  hasVariants: boolean,
  variantValueLookup: Map<string, string>
): any[] {
  return rawItems.map((item: any, index: number) => {
    const normalizedItem: any = {
      itemCode: hasVariants ? `${newProductCode}${index}` : newProductCode,
      itemActive: toFlag(item?.itemActive, 1),
      itemPrice: toNumber(item?.itemPrice),
      itemDiscountPrice: toNumber(item?.itemDiscountPrice),
      itemShopPoints: resolveItemShopPoints(item, source),
      itemSessions: toNumber(item?.itemSessions),
      itemBeneficiaries: toNumber(item?.itemBeneficiaries),
      itemHolder: toFlag(item?.itemHolder ?? item?.itemHolderRequired),
      itemRelationship: normalizeRelationship(item?.itemRelationship),
    };

    const itemVariantValues = resolveItemVariantValues(item, variantValueLookup);
    if (itemVariantValues?.length) {
      normalizedItem.itemVariantValues = itemVariantValues;
    }

    return normalizedItem;
  });
}

export function buildClonedProductCreatePayload(
  source: any,
  shopId: number,
  now: Date = new Date()
): ProductCloneCreatePayload {
  const product = source?.product ?? {};
  const rawVariants = Array.isArray(source?.variants) ? source.variants : [];
  const rawItems = Array.isArray(source?.items) ? source.items : [];
  const productCode = buildClonedProductCode(product?.productCode);
  const variants = normalizeVariants(rawVariants);
  const variantValueLookup = buildVariantValueLookup(rawVariants);
  const items = normalizeItems(
    rawItems,
    source,
    productCode,
    variants.length > 0,
    variantValueLookup
  );
  const externalUrl = trimToNull(product?.externalUrl ?? product?.external_url);

  return {
    productCode,
    shopId,
    productCategoryId: toNumber(product?.productCategoryId),
    productSubCategoryId: toNumber(product?.productSubCategoryId),
    productSubSubCategoryId: toNumber(product?.productSubSubCategoryId),
    productIndustryId: toNumber(product?.productIndustryTypeId),
    productSupplierId: toNumber(product?.productSupplierId),
    productCollectionId: toNumber(product?.productCollectionId),
    productTaxId: toNumber(product?.productTaxId),
    productName: toStringValue(product?.productName),
    productDescription: escapeProductCloneString(product?.productDescription),
    productBaseCost: toNumber(product?.productBaseCost),
    productSellPrice: toNumber(product?.productSellPrice),
    productDiscountPrice: toNumber(product?.productDiscountPrice),
    productCreationDate: buildProductCreationDate(now),
    productStatus: toFlag(
      product?.productIsActive ?? product?.productStatus,
      1
    ),
    items,
    variants,
    imgURL: toStringValue(product?.imgURL, 'X'),
    imgPosition: toNumber(product?.imgPosition, 1),
    imgType: toStringValue(product?.imgType, 'X'),
    productType: toNumber(product?.productType, 1),
    productSessions: toNumber(product?.productSessions),
    productFile: toStringValue(product?.productFile),
    productFiles: buildProductFilesPayload(
      normalizeProductFiles({ productFiles: product?.productFiles })
    ),
    productPackageItems: Array.isArray(source?.packageProducts)
      ? source.packageProducts
          .map((packageProduct: any) =>
            toNumber(packageProduct?.productId ?? packageProduct)
          )
          .filter((packageProductId: number) => packageProductId > 0)
      : [],
    productConditions: escapeProductCloneString(product?.productConditions),
    externalUrl,
    externalRedirectEnabled:
      !!product?.externalRedirectEnabled && externalUrl != null,
    hidePrice: !!product?.hidePrice,
    productPaymentMode: toNumber(product?.productPaymentMode, 1),
  };
}

@Injectable({
  providedIn: 'root',
})
export class ProductCloneService {
  constructor(private readonly lambdaService: LambdaService) {}

  async cloneProduct(
    productId: number,
    shopId: number,
    now: Date = new Date()
  ): Promise<ProductCloneResult> {
    const source = await this.lambdaService.getProductById(productId, shopId);
    const payload = buildClonedProductCreatePayload(source, shopId, now);
    const response = await this.lambdaService.createProduct(
      payload.productCode,
      payload.shopId,
      payload.productCategoryId,
      payload.productSubCategoryId,
      payload.productSubSubCategoryId,
      payload.productIndustryId,
      payload.productSupplierId,
      payload.productCollectionId,
      payload.productTaxId,
      payload.productName,
      payload.productDescription,
      payload.productBaseCost,
      payload.productSellPrice,
      payload.productDiscountPrice,
      payload.productCreationDate,
      payload.productStatus,
      payload.items,
      payload.variants,
      payload.imgURL,
      payload.imgPosition,
      payload.imgType,
      payload.productType,
      payload.productSessions,
      payload.productFile,
      payload.productFiles,
      payload.productPackageItems,
      payload.productConditions,
      payload.externalUrl,
      payload.externalRedirectEnabled,
      payload.hidePrice,
      payload.productPaymentMode
    );

    return {
      payload,
      response,
      productCode: payload.productCode,
    };
  }
}
